/**
 * Traduction FR → EN du contenu du site.
 *
 * `phrases` couvre les libellés exacts de l'interface (navigation, titres,
 * boutons, messages). `glossary` sert de repli mot à mot pour les phrases
 * rédigées qui ne figurent pas dans le dictionnaire.
 */
export const phrases: Record<string, string> = {
  // Navigation principale
  Ville: 'City',
  Services: 'Services',
  'Loisir et culture': 'Recreation and culture',
  'Communiquer avec nous': 'Contact us',
  Actualités: 'News',
  'Alertes citoyennes et avis publics': 'Citizen alerts and public notices',
  'Assemblées du conseil municipal': 'City council meetings',
  'Conseil municipal et élus': 'City council and elected officials',
  'Grands projets et chantiers': 'Major projects and worksites',
  'Découvrir Brossard': 'Discover Brossard',
  Emplois: 'Jobs',
  'Collecte des matières résiduelles': 'Waste collection',
  'Taxes, eau et évaluation': 'Taxes, water and assessment',
  'Permis et règlements': 'Permits and by-laws',
  'Urbanisme et demandes': 'Urban planning and applications',
  Animaux: 'Animals',
  'Sécurité publique': 'Public safety',
  'Réseau routier et stationnement': 'Roads and parking',
  'Activités et inscriptions': 'Activities and registration',
  'Complexe aquatique de Brossard': 'Brossard Aquatic Complex',
  'Bibliothèque de Brossard': 'Brossard Library',
  'Camps de jour': 'Day camps',
  'Spectacles et billetterie': 'Shows and tickets',
  'Réservation de salles et plateaux sportifs': 'Room and sports facility booking',
  Organismes: 'Community organizations',
  'Nous joindre': 'Contact us',
  'Requêtes et signalements': 'Requests and reports',
  'Requêtes et demandes': 'Requests and applications',
  'Suivi des requêtes': 'Request tracking',
  'Animal perdu': 'Lost pet',
  'Mes propriétés': 'My properties',
  'Politique de confidentialité': 'Privacy policy',
  'Où se trouve le marquage effacé ?': 'Where is the faded marking?',
  'Passage pour piétons près d’une école': 'Crosswalk near a school',
  'Ligne de circulation': 'Lane line',
  'Ligne d’arrêt': 'Stop line',
  'Votre carte de la Bibliothèque de Brossard ne sera plus mise à jour.':
    'Your Brossard Library card will no longer be updated.',
  'Accordé le 21 août 2026': 'Granted on August 21, 2026',
  'Panneau d’arrêt endommagé': 'Damaged stop sign',
  'Signalisation routière': 'Road signage',
  'Qualité du service': 'Service quality',
  'Êtes-vous satisfaite du service ?': 'Are you satisfied with the service?',
  'Merci pour votre avis !': 'Thank you for your feedback!',
  'Merci pour votre signalement ! Le panneau d’arrêt de la rue Bordeaux a été remplacé et est maintenant bien visible. N’hésitez pas à nous dire ce que vous pensez de la qualité du service.':
    'Thank you for your report! The stop sign on rue Bordeaux has been replaced and is now clearly visible. Feel free to tell us what you think of the quality of our service.',
  'Mes véhicules': 'My vehicles',
  'Vignette active': 'Active permit',
  'Quel animal avez-vous perdu ?': 'Which pet did you lose?',
  'Être avisée par courriel et par SMS à chaque changement de statut de vos requêtes et lorsque la Ville vous écrit.':
    'Be notified by email and text message whenever one of your requests changes status or the City sends you a message.',
  'Accordé le 23 septembre 2026': 'Granted on September 23, 2026',
  'Participation citoyenne': 'Citizen participation',
  Infolettre: 'Newsletter',
  'Magazine Ma Ville': 'Ma Ville magazine',

  // En-tête et accès rapides
  Plan: 'Map',
  'Ma carte': 'My card',
  'Mon espace': 'My space',
  'Mon profil': 'My profile',
  'Mes notifications': 'My notifications',
  'Mes collectes': 'My collections',
  'Mes demandes': 'My requests',
  'Mes loisirs': 'My activities',
  'Mes animaux': 'My pets',
  'Ma carte numérique': 'My digital card',
  'Mon espace citoyen': 'My citizen space',
  'Votre espace citoyen': 'Your citizen space',
  Accueil: 'Home',
  Requête: 'Request',
  Notifications: 'Notifications',
  'Inscriptions et réservations': 'Registration and bookings',
  Collectes: 'Collections',
  'Médailles animaux': 'Pet tags',
  'Demande de permis': 'Permit application',
  'Alertes citoyennes': 'Citizen alerts',
  'Alerte citoyenne': 'Citizen alert',
  'Rechercher sur le site': 'Search the site',
  'Ouvrir le menu': 'Open menu',
  'Fermer le menu': 'Close menu',
  'Navigation principale': 'Main navigation',
  'Navigation mobile': 'Mobile navigation',
  'Quitter mon espace': 'Leave my space',
  'Se déconnecter': 'Sign out',
  'Se connecter': 'Sign in',
  Français: 'French',
  Anglais: 'English',

  // Hero
  'Collectes d’automne': 'Fall collections',
  'La collecte des feuilles commence bientôt': 'Leaf collection starts soon',
  'Du 19 octobre au 27 novembre 2026': 'October 19 to November 27, 2026',
  'Déposez vos feuilles mortes en sacs de papier en bordure de rue, le jour de votre collecte. Consultez le calendrier de votre secteur pour préparer vos sacs.':
    'Put your dead leaves in paper bags at the curb on your collection day. Check your sector’s calendar to plan ahead.',
  'Voir le calendrier des collectes': 'See the collection calendar',
  'Fête de l’Halloween au parc de la Cité': 'Halloween party at Parc de la Cité',
  'De 16 h à 19 h': '4 p.m. to 7 p.m.',
  'La collecte des feuilles d’automne commence le 19 octobre': 'Fall leaf collection starts October 19',
  'Collecte aujourd’hui': 'Collection today',
  'Collecte en cours aujourd’hui': 'Collection in progress today',
  'Camion en route': 'Truck on its way',
  'Le camion est en route · ordures, organiques, résidus verts et recyclage':
    'The truck is on its way · garbage, organics, yard waste and recycling',
  'La collecte des encombrants du 9 octobre est reportée au 16 octobre.':
    'The October 9 bulky-item collection is postponed to October 16.',
  'Services en ligne': 'Online services',
  'Brossard dans votre poche avec bciti+': 'Brossard in your pocket with bciti+',
  'Disponible maintenant': 'Available now',
  'Requêtes, collectes, alertes, inscriptions aux activités et réservation de plateaux : tous vos services municipaux dans une seule application.':
    'Requests, collections, alerts, activity registration and facility booking: all your city services in a single app.',
  "Télécharger l'application": 'Download the app',
  "La programmation d'automne 2026 est en ligne": 'The fall 2026 program is online',
  'Inscriptions ouvertes depuis le 22 septembre': 'Registration open since September 22',
  'Activités sportives, cours de musique, ateliers artistiques et programmation pour les 50 ans et plus.':
    'Sports activities, music lessons, art workshops and programming for ages 50 and over.',
  'Voir la programmation': 'See the program',
  'Demander à ATNA…': 'Ask ATNA…',
  'Demander à ATNA': 'Ask ATNA',
  'Ma prochaine collecte': 'My next collection',
  'Signaler un bris': 'Report a problem',
  'Inscrire mon enfant': 'Register my child',
  'ATNA connaît les services de Brossard': 'ATNA knows Brossard’s services',
  'Assistant ATNA': 'ATNA assistant',
  'Services les plus demandés': 'Most requested services',

  // ATNA — conversation
  'Signaler un problème': 'Report a problem',
  'Inscription aux activités': 'Activity registration',
  Conversation: 'Conversation',
  Messages: 'Messages',
  'Fermer la conversation': 'Close conversation',
  'Nouvelle conversation': 'New conversation',
  'Répondre à ATNA, ou préciser votre demande…': 'Reply to ATNA, or add details…',
  'Autre chose à signaler ?': 'Anything else to report?',
  'ATNA peut se tromper. Vérifiez le résumé avant d’envoyer votre demande.':
    'ATNA can make mistakes. Review the summary before sending your request.',
  'ATNA peut se tromper. Vérifiez l’horaire avant de confirmer une inscription.':
    'ATNA can make mistakes. Check the schedule before confirming a registration.',
  'Où se trouve le problème ?': 'Where is the problem?',
  'Choisissez l’endroit en un clic.': 'Choose the location in one click.',
  'Utiliser ma position actuelle': 'Use my current location',
  'Localisation en cours…': 'Locating…',
  Principale: 'Primary',
  'Quelle activité voulez-vous ?': 'Which activity would you like?',
  Inscription: 'Registration',
  Recherche: 'Search',
  Recommandé: 'Recommended',
  Choisir: 'Choose',
  'Voir mes activités': 'See my activities',
  'Ajouter au calendrier': 'Add to calendar',
  'Inscrire à autre chose': 'Register for something else',
  'Inscrire à une autre activité ?': 'Register for another activity?',
  'Nommez une activité, ex. « cours de piscine »…': 'Name an activity, e.g. “swimming lessons”…',
  'Aucune place trouvée pour cette activité.': 'No spots found for this activity.',
  'Nommez une autre activité et je regarde de nouveau.': 'Name another activity and I’ll look again.',
  'Recherche assistée par ATNA': 'ATNA-assisted search',
  'Inscrire mon enfant, trouver une activité…': 'Register my child, find an activity…',
  'Propulsé par ATNA': 'Powered by ATNA',
  'Trouver une activité': 'Find an activity',
  'Réserver un espace': 'Book a space',
  'Louer de l’équipement': 'Rent equipment',
  Natation: 'Swimming',
  Soccer: 'Soccer',
  Patinage: 'Skating',
  'Arts plastiques': 'Visual arts',
  Musique: 'Music',

  // Collectes
  'Collecte en cours': 'Collection in progress',
  'Vos prochaines collectes': 'Your upcoming collections',
  'Collecte suivante': 'Next collection',
  'Arrivée estimée': 'Estimated arrival',
  'Distance actuelle': 'Current distance',
  'Votre adresse': 'Your address',
  'Sur place': 'On site',
  'Arrive bientôt': 'Arriving soon',
  'En route': 'On the way',
  Rappels: 'Reminders',
  Calendrier: 'Calendar',
  Carte: 'Map',
  Recyclage: 'Recycling',
  Ordures: 'Garbage',
  'Matières organiques': 'Organic waste',
  'Résidus verts': 'Yard waste',
  'Raccourcis collectes': 'Collection shortcuts',
  'Calendrier des collectes': 'Collection calendar',

  // Demandes
  Détails: 'Details',
  Description: 'Description',
  'Description de la requête': 'Request description',
  Emplacement: 'Location',
  Dates: 'Dates',
  'Fichiers joints': 'Attached files',
  'Aucun fichier joint.': 'No attached files.',
  'Ajouter un fichier': 'Add a file',
  Historique: 'History',
  'Créée le': 'Created on',
  Résolue: 'Resolved',
  Actualiser: 'Refresh',
  'Travaux publics': 'Public works',
  'Signalements publics': 'Public reports',
  'Je confirme': 'I confirm',
  Nouveau: 'New',
  'Équipe en route': 'Crew on the way',
  Réglé: 'Resolved',
  'En direct': 'Live',
  Tous: 'All',
  Danger: 'Hazard',
  Voirie: 'Roads',
  Éclairage: 'Lighting',
  Arbres: 'Trees',
  'Réponse attendue': 'Response required',
  'Document à remplir et à retourner': 'Form to complete and return',
  'Joindre le formulaire rempli': 'Attach the completed form',
  'La Ville attend un document de votre part': 'The City is waiting for a document from you',

  // Profil et compte
  'Informations personnelles': 'Personal information',
  'Mes adresses': 'My addresses',
  'Gestion du compte': 'Account management',
  'Préférences de communication': 'Communication preferences',
  'Connexion requise': 'Sign-in required',
  'J’ai déjà un compte': 'I already have an account',
  'Créer mon compte': 'Create my account',
  'Courriel ou numéro de mobile': 'Email or mobile number',
  'Recevoir mon code': 'Get my code',
  'Entrez votre code': 'Enter your code',
  Confirmer: 'Confirm',
  'Connexion réussie': 'Signed in',
  'Compte créé': 'Account created',
  'Ouverture de vos services…': 'Opening your services…',
  'Ce module est réservé aux citoyens connectés.': 'This section is reserved for signed-in residents.',
  'Étape précédente': 'Previous step',
  Fermer: 'Close',

  // Plan
  'Plan de la ville': 'City map',
  'Lieux d’intérêt': 'Points of interest',
  'Services municipaux': 'City services',
  'Sports et loisirs': 'Sports and recreation',
  Culture: 'Culture',
  Parcs: 'Parks',
  Écocentres: 'Eco-centres',
  Ouvert: 'Open',
  Fermé: 'Closed',
  Itinéraire: 'Directions',

  // ── Module Requêtes ──────────────────────────────────────────────
  // Interface
  'Fil d’Ariane': 'Breadcrumb',
  'Créer une demande manuellement': 'Create a request manually',
  'Suivez l’avancement de ce que vous avez signalé.': 'Track the progress of what you reported.',
  'Décrivez simplement ce que vous voyez. ATNA vous aide à transmettre la demande au bon service.':
    'Simply describe what you see. ATNA helps you send the request to the right department.',
  'Que voulez-vous signaler ?': 'What would you like to report?',
  '· Requête': '· Request',
  'Actualiser la requête': 'Refresh the request',
  'Sections de la requête': 'Request sections',
  'Statut actuel :': 'Current status:',
  'Les mises à jour de cette demande apparaissent aussi dans':
    'Updates to this request also appear in',
  'Demande créée par': 'Request created by',
  'Historique de la requête': 'Request history',
  étapes: 'steps',
  '-- / -- / --': '-- / -- / --',
  'Échangez avec le service': 'Chat with the',
  'au sujet de': 'department about',
  'Réponse en 2 jours ouvrables': 'Reply within 2 business days',
  'La Ville attend une précision de votre part': 'The City is waiting for more details from you',
  'Remplissez le formulaire joint au dernier message, puis retournez-le ici.':
    'Fill out the form attached to the last message, then send it back here.',
  'Répondez au dernier message pour que l’équipe puisse poursuivre le traitement.':
    'Reply to the last message so the team can continue processing your request.',
  'Écrire un message au service': 'Write a message to',
  'Retirer le fichier joint': 'Remove the attachment',
  'Joindre un fichier': 'Attach a file',
  'Envoyer le message': 'Send the message',
  'Voici le document demandé.': 'Here is the requested document.',
  'À l’instant': 'Just now',
  Vous: 'You',
  'Signalements autour de vous': 'Reports around you',
  'signalements actifs ·': 'active reports ·',
  'nouveaux aujourd’hui': 'new today',
  'Carte des signalements publics à Brossard': 'Map of public reports in Brossard',
  À: 'At',
  'Signalement précédent': 'Previous report',
  'Signalement suivant': 'Next report',
  'personnes le confirment': 'people confirm it',
  Confirmé: 'Confirmed',
  'Aucun signalement dans cette catégorie pour le moment.': 'No reports in this category right now.',

  // Statuts et raccourcis
  Reçue: 'Received',
  'En traitement': 'In progress',
  Arbre: 'Tree',
  Eau: 'Water',
  Autre: 'Other',

  // Compréhension ATNA
  'Nid-de-poule': 'Pothole',
  'Arbre ou branche à dégager': 'Tree or branch to clear',
  'Fuite d’eau': 'Water leak',
  'Bac non ramassé': 'Bin not picked up',
  'Parcs et espaces verts': 'Parks and green spaces',
  Aqueduc: 'Water mains',
  'Quelle est la taille approximative du nid-de-poule ?': 'Roughly how big is the pothole?',
  'Petit (moins de 30 cm)': 'Small (under 30 cm)',
  'Moyen (30 à 60 cm)': 'Medium (30 to 60 cm)',
  'Grand (plus de 60 cm)': 'Large (over 60 cm)',
  'Aujourd’hui': 'Today',
  'Cette semaine': 'This week',
  'Depuis plus d’un mois': 'For over a month',
  'Le lampadaire est-il complètement éteint ou clignote-t-il ?': 'Is the streetlight completely out, or is it flickering?',
  'Complètement éteint': 'Completely out',
  'Il clignote': 'It’s flickering',
  'Il reste allumé le jour': 'It stays on during the day',
  'L’arbre bloque-t-il la circulation ?': 'Is the tree blocking traffic?',
  'Oui, la rue est bloquée': 'Yes, the street is blocked',
  'Non, sur le trottoir': 'No, it’s on the sidewalk',
  'Non, sur un terrain': 'No, it’s on a property',
  'L’écoulement est-il continu ?': 'Is the water flowing continuously?',
  'Oui, en continu': 'Yes, continuously',
  'Par intermittence': 'On and off',
  'Je ne sais pas': 'I don’t know',
  'Quel bac n’a pas été ramassé ?': 'Which bin wasn’t picked up?',

  // Requête #REQ-2903 — animal trouvé
  'Animal trouvé': 'Found animal',
  'Service animalier': 'Animal services',
  'Animal errant ou trouvé': 'Stray or found animal',
  'Envoyée aujourd’hui': 'Sent today',
  'Chien trouvé sans médaille lisible près de l’aire de jeux du parc. L’animal est calme et gardé en laisse en attendant une prise en charge.':
    'Dog found near the park playground with no readable tag. The animal is calm and kept on a leash until it can be picked up.',
  'Signalement reçu': 'Report received',
  'Merci pour votre signalement. Un patrouilleur animalier est assigné et vous contactera si l’animal est toujours sur place.':
    'Thank you for your report. An animal control officer has been assigned and will contact you if the animal is still there.',

  // Requête #REQ-2790 — échanges avec la Ville
  'Envoyée le 23 septembre': 'Sent on September 23',
  'Intervention planifiée': 'Work scheduled',
  'Bonjour, votre signalement a été transmis à l’équipe d’éclairage public. Une vérification est prévue cette semaine.':
    'Hello, your report has been forwarded to the street lighting team. An inspection is scheduled for this week.',
  'Merci. Le lampadaire voisin, devant le 4620, commence aussi à clignoter depuis hier soir.':
    'Thank you. The neighbouring streetlight, in front of 4620, also started flickering last night.',
  'Merci pour la précision. Pouvez-vous nous indiquer le numéro inscrit sur la plaque du poteau ? Il nous aide à identifier le bon circuit électrique.':
    'Thanks for the details. Could you tell us the number on the pole’s plate? It helps us identify the right electrical circuit.',
  'Le poteau se trouve en bordure de votre terrain. Pour permettre à l’équipe d’y accéder avec la nacelle, veuillez remplir et nous retourner l’autorisation ci-jointe dans les 15 jours.':
    'The pole is at the edge of your property. So the team can reach it with the bucket truck, please fill out and return the attached authorization within 15 days.',

  // Signalement ATNA — nid-de-poule
  'Voirie et chaussée': 'Roads and pavement',
  'Important nid-de-poule devant la propriété, dans la voie de droite. Le trou s’est élargi depuis les dernières pluies et plusieurs véhicules l’évitent en empiétant sur la voie opposée.':
    'Large pothole in front of the property, in the right lane. It has widened since the last rains, and several vehicles swerve into the opposite lane to avoid it.',
  'Transmise au service des travaux publics': 'Forwarded to the Public Works department',
  'Inspection planifiée': 'Inspection scheduled',
  'Travaux publics · Julie Béland': 'Public works · Julie Béland',
  'Bonjour, votre signalement a été transmis à l’équipe de voirie. Une inspection est prévue cette semaine.':
    'Hello, your report has been forwarded to the roads team. An inspection is scheduled for this week.',
  'Merci. Le trou s’est encore agrandi depuis la pluie de dimanche, il fait maintenant environ 60 cm de large.':
    'Thank you. The pothole has grown again since Sunday’s rain — it’s now about 60 cm wide.',
  'Merci pour la précision. Pouvez-vous confirmer si le nid-de-poule se trouve devant votre entrée ou plus près de l’intersection ? Une photo prise de loin nous aiderait à le localiser.':
    'Thanks for the details. Can you confirm whether the pothole is in front of your driveway or closer to the intersection? A photo taken from a distance would help us locate it.',
  'Une réclamation pour dommage à un véhicule peut être ouverte si applicable. Veuillez remplir et nous retourner le formulaire ci-joint dans les 15 jours.':
    'A claim for vehicle damage can be opened if applicable. Please fill out the attached form and return it within 15 days.',

  // Requête #REQ-2790 — lampadaire
  'Lampadaire défectueux': 'Faulty streetlight',
  'Éclairage public': 'Street lighting',
  'Le lampadaire devant le 4610 restait éteint la nuit, laissant le trottoir dans le noir entre les deux intersections.':
    'The streetlight in front of 4610 stayed off at night, leaving the sidewalk dark between the two intersections.',
  'Demande reçue': 'Request received',
  'Transmise au service de l’éclairage': 'Forwarded to the lighting department',
  'L’ampoule a été remplacée aujourd’hui par l’équipe d’entretien. La requête est fermée, n’hésitez pas à nous écrire si le problème revient.':
    'The bulb was replaced today by the maintenance team. The request is now closed — feel free to write to us if the problem comes back.',
  'L’ampoule a été remplacée le 18 août par l’équipe d’entretien.':
    'The bulb was replaced on August 18 by the maintenance team.',

  // Requête #REQ-2947 — bac non ramassé
  'Requête ouverte par ATNA': 'Request opened by ATNA',
  'Transmise à la Ville de Brossard': 'Forwarded to the City of Brossard',
  'Ville de Brossard': 'City of Brossard',
  'Écrire un message à la Ville de Brossard': 'Write a message to the City of Brossard',
  'Échangez avec la Ville de Brossard au sujet de': 'Chat with the City of Brossard about',
  'Merci pour votre signalement. Un rattrapage est prévu dans les 48 heures ouvrables. Laissez votre bac au bord de la rue d’ici là.':
    'Thank you for your report. A make-up pickup is scheduled within 48 business hours. Leave your bin at the curb until then.',

  // Signalements publics
  'Nid-de-poule profond': 'Deep pothole',
  'Lampadaire éteint': 'Streetlight out',
  'Branche tombée sur le trottoir': 'Fallen branch on the sidewalk',
  'Affaissement de chaussée': 'Sunken pavement',
  'Lampadaire qui clignote': 'Flickering streetlight',
  'Feu de circulation hors service': 'Traffic light out of order',
  'Marquage de rue effacé': 'Faded road markings',
  'Arbre à élaguer près des fils': 'Tree to trim near power lines',
  'Boulevard Grande-Allée, près de la rue Bordeaux': 'Boulevard Grande-Allée, near rue Bordeaux',
  'Rue de Lyon, angle avenue Lakeshore': 'Rue de Lyon, corner of avenue Lakeshore',
  'Boulevard Rome, angle boulevard Pelletier': 'Boulevard Rome, corner of boulevard Pelletier',
  hier: 'yesterday',

  // Questions à ATNA sur une requête
  'Où en est ma demande ?': 'What’s the status of my request?',
  'Quand sera-t-elle traitée ?': 'When will it be handled?',
  'Est-ce que je dois faire autre chose ?': 'Do I need to do anything else?',
  'Votre demande est en traitement par le service des travaux publics depuis le 28 août.':
    'Your request has been in progress with the Public Works department since August 28.',
  'Aucune date d’exécution n’est disponible pour le moment. Vous serez avisé dès que le statut change.':
    'No completion date is available yet. You’ll be notified as soon as the status changes.',
  'Non, aucune action n’est requise de votre part pour l’instant.':
    'No, nothing is required from you for now.',
}

/** Repli mot à mot pour le contenu rédigé. */
export const glossary: Record<string, string> = {
  et: 'and',
  ou: 'or',
  le: 'the',
  la: 'the',
  les: 'the',
  un: 'a',
  une: 'a',
  des: 'the',
  du: 'of the',
  de: 'of',
  au: 'at the',
  aux: 'at the',
  votre: 'your',
  vos: 'your',
  vous: 'you',
  mon: 'my',
  ma: 'my',
  mes: 'my',
  nos: 'our',
  notre: 'our',
  ce: 'this',
  cette: 'this',
  pour: 'for',
  avec: 'with',
  sans: 'without',
  sur: 'on',
  dans: 'in',
  par: 'by',
  plus: 'more',
  tout: 'all',
  tous: 'all',
  toute: 'all',
  toutes: 'all',
  ville: 'city',
  citoyen: 'resident',
  citoyens: 'residents',
  citoyenne: 'resident',
  service: 'service',
  services: 'services',
  demande: 'request',
  demandes: 'requests',
  requête: 'request',
  requêtes: 'requests',
  collecte: 'collection',
  collectes: 'collections',
  adresse: 'address',
  adresses: 'addresses',
  carte: 'card',
  activité: 'activity',
  activités: 'activities',
  inscription: 'registration',
  inscriptions: 'registration',
  enfant: 'child',
  famille: 'family',
  animal: 'animal',
  animaux: 'animals',
  profil: 'profile',
  compte: 'account',
  courriel: 'email',
  téléphone: 'phone',
  message: 'message',
  messages: 'messages',
  photo: 'photo',
  document: 'document',
  documents: 'documents',
  place: 'spot',
  places: 'spots',
  heure: 'hour',
  heures: 'hours',
  jour: 'day',
  jours: 'days',
  semaine: 'week',
  mois: 'month',
  aujourd: 'today',
  demain: 'tomorrow',
  prochaine: 'next',
  prochain: 'next',
  nouvelle: 'new',
  nouveau: 'new',
  voir: 'see',
  ajouter: 'add',
  envoyer: 'send',
  modifier: 'edit',
  supprimer: 'delete',
  confirmer: 'confirm',
  annuler: 'cancel',
  fermer: 'close',
  ouvrir: 'open',
  chercher: 'search',
  trouver: 'find',
  signaler: 'report',
  inscrire: 'register',
  gérer: 'manage',
  suivre: 'track',
  lundi: 'Monday',
  mardi: 'Tuesday',
  mercredi: 'Wednesday',
  jeudi: 'Thursday',
  vendredi: 'Friday',
  samedi: 'Saturday',
  dimanche: 'Sunday',
  janvier: 'January',
  février: 'February',
  mars: 'March',
  avril: 'April',
  mai: 'May',
  juin: 'June',
  juillet: 'July',
  août: 'August',
  septembre: 'September',
  octobre: 'October',
  novembre: 'November',
  décembre: 'December',
}
